import {Bid} from 'common'
import React, {useMemo, useState} from 'react'
import styles from './ProductBidsModal.module.scss'
import {
  getRelativeTime,
  getTimeStampFromObject,
  useWindowSize,
  withThousandSeperatorString
} from 'components/utils'
import {Cancel} from 'assets/svgs'
import UserProfilePicture from 'components/UserProfileImage'
import {UserPlaceholder} from 'assets/images'
import Pagination from 'components/Pagination'
import Button from 'components/Button'

interface ProductBidsModalProps {
  handleClose: () => void
  bids: Array<Bid>
}

const ProductBidsModal = ({handleClose, bids}: ProductBidsModalProps) => {
  const {height, width} = useWindowSize()
  const isDesktop = width && width > 481 ? true : false
  const PAGINATION_PAGE_SIZE =
    (height && (height < 700 ? 2 : height < 950 ? 3 : 4)) || 3
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [showAllBids, setShowAllBids] = useState<boolean>(false)

  const processedBids = useMemo(() => {
    if (showAllBids) {
      return bids.sort((a, b) => 
        getTimeStampFromObject(b.createdAt as any).toDate().getTime() - 
        getTimeStampFromObject(a.createdAt as any).toDate().getTime()
      )
    }

    const bidsByBuyer = new Map<string, {bid: Bid, count: number}>()
    
    bids.forEach(bid => {
      const bidKey = bid.buyerId + '_' + bid.buyerAccountId;
      const existing = bidsByBuyer.get(bidKey)
      if (!existing) {
        bidsByBuyer.set(bidKey, {bid, count: 1})
      } else {
        existing.count++
        const existingTime = getTimeStampFromObject(existing.bid.createdAt as any).toDate().getTime()
        const currentTime = getTimeStampFromObject(bid.createdAt as any).toDate().getTime()
        if (currentTime > existingTime) {
          existing.bid = bid
        }
      }
    })

    return Array.from(bidsByBuyer.values()).map(({bid, count}) => ({...bid, bidIteration: count-1}))
  }, [bids, showAllBids])

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PAGINATION_PAGE_SIZE
    const lastPageIndex = firstPageIndex + PAGINATION_PAGE_SIZE
    return processedBids.slice(firstPageIndex, lastPageIndex)
  }, [PAGINATION_PAGE_SIZE, currentPage, processedBids])

  const showPagination = processedBids.length > PAGINATION_PAGE_SIZE

  return (
    <React.Fragment>
      <div className={styles.heading}>
        <div className={styles.button_wrapper}>
          <Button
            text={showAllBids ? 'Show Latest Bids' : 'Bid History'}
            customBtnClass="btn-fill-green"
            withLoader={false}
            onClick={() => {
              setShowAllBids(!showAllBids)
              setCurrentPage(1)
            }}
          />
        </div>
        <Cancel onClick={handleClose} />
      </div>
      <div className={styles.container}>
        <div className={styles.bid_container}>
          {isDesktop ? (
            <React.Fragment>
              <div className={`${styles.card_header} ${!showAllBids ? styles.card_header_with_iteration : ''}`}>
                <div>Buyer Name</div>
                <div>Amount</div>
                <div>Time</div>
                {!showAllBids && <div>Iterations</div>}
              </div>
              <div className={styles.card_container}>
                {currentTableData.map((item, index) => {
                  const bidAmount = `${withThousandSeperatorString(
                    `${item?.bidAmount}`
                  )}`
                  const bidTime = getRelativeTime(
                    getTimeStampFromObject(item?.createdAt as any).toDate()
                  )
                  return (
                    <div className={`${styles.card} ${!showAllBids ? styles.card_with_iteration : ''}`} key={`bid-user-${index}`}>
                      <div>
                        <UserProfilePicture
                          width={40}
                          height={40}
                          alt="user-avatar"
                          userId={item?.buyerId}
                          className={styles.card_img}
                        />
                        <span>{item?.buyerName}{item?.buyerAccountName && <><br/><small> ({item?.buyerAccountName})</small></>}</span>
                      </div>
                      <div>{bidAmount}</div>
                      <div>{bidTime}</div>
                      {!showAllBids && <div>{(item as any).bidIteration}</div>}
                    </div>
                  )
                })}
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              {currentTableData.map((item, index) => {
                const bidAmount = `${withThousandSeperatorString(
                  `${item?.bidAmount}`
                )}`
                const bidTime = getRelativeTime(
                  getTimeStampFromObject(item?.createdAt as any).toDate()
                )
                return (
                  <div
                    className={styles.mobile_card}
                    key={`bid-user-${index}--mobile`}
                  >
                    <div>
                      <div className={styles.mobile_card_header}>
                        Buyer Name
                      </div>
                      <div className={styles.mobile_card_buyer}>
                        <img
                          width={40}
                          height={40}
                          className={styles.card_img}
                          alt="user-avatar"
                          src={item?.buyerPhotoUrl || UserPlaceholder}
                        />
                        <span>{item?.buyerName}{item?.buyerAccountName && <><br/><small> ({item?.buyerAccountName})</small></>}</span>
                      </div>
                    </div>
                    <div>
                      <span className={styles.mobile_card_header}>Amount:</span>{' '}
                      {bidAmount}
                    </div>
                    <div>
                      <span className={styles.mobile_card_header}>Time:</span>{' '}
                      {bidTime}
                    </div>
                    {!showAllBids && (
                      <div>
                        <span className={styles.mobile_card_header}>Iterations:</span>{' '}
                        {(item as any).bidIteration}
                      </div>
                    )}
                  </div>
                )
              })}
            </React.Fragment>
          )}
        </div>
        {showPagination && (
          <div className={styles['pagination__wrapper']}>
            <Pagination
              currentPage={currentPage}
              totalCount={processedBids.length}
              pageSize={PAGINATION_PAGE_SIZE}
              arrowsForNextAndPrev={false}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>
    </React.Fragment>
  )
}

export default ProductBidsModal
